import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import PagePanel from 'src/components/PagePanel';
import PageHeader from 'src/components/PageHeader';
import { Table } from 'src/components/TableNew';
import NoResult from 'src/components/NoResult/NoResult';

import { useDispatch } from 'src/store/useDispatch';
import { setAppAlert } from 'src/store/slice/appAlert';
import {
  fetchReferrals,
  formatReferralName,
  resolveReferralId,
  type ReferralRecord,
} from 'src/api/adminUsers';
import { REFERRALS_HEADERS } from 'src/utils/constants';
import { ROUTE_USERS } from 'src/utils/routes';
import { UserLink } from 'src/pages/Wallets/styled';

const Referrals = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [referrals, setReferrals] = useState<ReferralRecord[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetchReferrals({ page: currentPage, limit: 10 });
      setReferrals(res.referrals);
      setTotalCount(res.meta?.total_count ?? 0);
      setTotalPages(Math.max(1, res.meta?.page_count || 1));
    } catch (e: any) {
      dispatch(
        setAppAlert({
          message: e?.response?.data?.error ?? 'Failed to load referrals',
          isSuccess: false,
        }),
      );
      setReferrals([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [currentPage, dispatch]);

  useEffect(() => {
    load();
  }, [load]);

  // The endpoint takes page and limit only, so the list is newest-first and
  // offers no search or sort.
  const rows = useMemo(
    () =>
      referrals.map((item) => ({
        ...item,
        referral_display: formatReferralName(item.referral_name),
        referral_code: item.referral_bxlend_id === 'N/A' ? '—' : item.referral_bxlend_id || '—',
        referrer_display: formatReferralName(item.referred_by_name),
        referrer_code:
          item.referred_by_bxlend_id === 'N/A' ? '—' : item.referred_by_bxlend_id || '—',
      })),
    [referrals],
  );

  return (
    <PagePanel>
      <PageHeader title={`Referrals (${totalCount})`} hideSearch />

      {rows.length > 0 || loading ? (
        <Table
          loading={loading}
          headers={REFERRALS_HEADERS}
          items={rows}
          columns="1.2fr 1fr 1.2fr 1fr"
          fieldsToShow={[
            'referral_display',
            'referral_code',
            'referrer_display',
            'referrer_code',
          ]}
          totalPages={totalPages}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          disableRowDetails
          renderCell={(item, field) => {
            if (field === 'referral_display' || field === 'referrer_display') {
              const isReferral = field === 'referral_display';
              const userId = resolveReferralId(
                isReferral ? item.referral_id : item.referred_by_id,
              );
              const value = String(item[field] || '—');
              if (!userId || value === '—') return <span>{value}</span>;
              return (
                <UserLink type="button" onClick={() => navigate(`${ROUTE_USERS}/${userId}`)}>
                  {value}
                </UserLink>
              );
            }
            return null;
          }}
        />
      ) : (
        <NoResult />
      )}
    </PagePanel>
  );
};

export default Referrals;
